<template>
    <div>
        <InviteEmailsInput
            ref="inviteEmailsInput"
            class="mt-3"
            v-model="emailsInput"
            :valid.sync="valid"
            :disabled="numInvitationsToSend !== 0"
            :showGenerateLinkOnlyToggle="true"
            :generateInvitationLinksOnly.sync="generateInvitationLinksOnly"
            :invitationErrorState="{
                showDialog: showInvitationErrorsDialog,
                errorType: validationErrorType,
                invalidEmails: invalidEmailsList,
                foundInvalidEmails,
                backendError,
                emailsValidationError
            }"
            @input="updateEmailString"
            @proceedWithTheInvitation="inviteUsers($event.value)"
            @validateEmailsAgain="validateEmailsAndInviteUsers($event.value)" />

        <div v-if="emailsInput.length" class="mt-3 secondary--text">
            <slot name="confirmation"></slot>
        </div>
        <v-alert v-if="error" color="error" icon="warning" class="mt-4" text>
            <div class="d-flex flex-column">
                <span class="font-weight-bold">Invitation Failure Error</span>
                Failed to invite the following users
                <ul>
                    <li v-for="(email, index) in failedInvitations" :key="index">
                        {{ email }}
                    </li>
                </ul>
                <span v-if="invitationErrorContent">{{ invitationErrorContent }}</span>
            </div>
        </v-alert>
        <div class="d-flex" :class="options.ctaAlign === 'right' ? 'justify-end' : 'justify-start'">
            <v-btn
                :loading="numInvitationsToSend > 0 || validatingEmails"
                @click="validateEmailsAndInviteUsers(true)"
                :disabled="!valid || numInvitationsToSend !== 0 || validatingEmails"
                color="primary"
                text>
                <v-icon small left>add</v-icon>
                Users
            </v-btn>
        </div>
    </div>
</template>

<script>
import { splitEmails } from '@/utils'
import { emailValidation } from '@/mixins/validateEmail'
import InviteEmailsInput from '@/components/InviteEmailsInput.vue'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as invitationsErrors from '@/mixins/errors/invitations'
import * as organizationErrors from '@/modules/home/errors/organizations'
import * as permissionsErrors from '@/utils/errors/permissions'

const { getBackendErrorMessage } = createBackendErrorHandler(invitationsErrors, organizationErrors, permissionsErrors)

export default {
    mixins: [emailValidation],
    components: { InviteEmailsInput },
    props: {
        requestUrl: {
            type: String,
            required: true
        },
        options: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            error: false,
            emailsInput: '',
            emailList: [],
            failedInvitations: [],
            invitationErrorContent: '',
            valid: false,
            generateInvitationLinksOnly: 0,
            numInvitationsToSend: 0
        }
    },
    computed: {
        showInvitationErrorsDialog() {
            return this.foundInvalidEmails || this.backendError || this.emailsValidationError
        },
        validationErrorType() {
            if (this.foundInvalidEmails) {
                return this.emailValidationErrorTypes.INVALID_EMAILS_ERROR
            } else if (this.backendError || this.emailsValidationError) {
                return this.emailValidationErrorTypes.EMAIL_VALIDATION_BACKEND_ERROR
            }
            return null
        }
    },
    methods: {
        updateEmailString: function () {
            this.emailList = splitEmails(this.emailsInput).map(email => email.email)
        },
        inviteUsers: function (confirmed) {
            this.$emit('inProgress', true)
            if (confirmed) {
                this.error = false
                this.invitationErrorContent = ''
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Inviting users...',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'info'
                })
                this.numInvitationsToSend = this.emailList.length
                this.emailList.forEach(email => {
                    const postBody = { requestee_email: email, generate_link_only: this.generateInvitationLinksOnly }
                    this.$axios
                        .post(this.requestUrl, postBody, { timeout: 300000 })
                        .then(response => {})
                        .catch(error => {
                            this.failedInvitations.push(email)
                            this.error = true
                            const invitationErrorContent = getBackendErrorMessage(error, '')
                            if (invitationErrorContent) this.invitationErrorContent = invitationErrorContent
                        })
                        .finally(() => {
                            this.numInvitationsToSend -= 1
                            if (!this.error && this.numInvitationsToSend === 0) {
                                this.$emit('inProgress', false)
                                this.$store.dispatch('showSnackBar', {
                                    snackBarText: 'Invitations have been successfully sent',
                                    snackBarTimeout: 5000,
                                    snackBarIcon: 'check_circle'
                                })
                                this.foundInvalidEmails = false
                                this.emailsInput = ''
                                this.$refs.inviteEmailsInput.resetValidation()
                            }
                        })
                })
            }
        },
        validateEmailsAndInviteUsers: function (confirmed) {
            if (confirmed) {
                this.validateEmails(this.emailList).finally(() => {
                    if (!this.emailsValidationError && !this.backendError && !this.foundInvalidEmails) {
                        this.inviteUsers(true)
                    }
                })
            }
        }
    }
}
</script>
